/**
 * Turning a lease into money owed.
 *
 * `buildSchedule` is the only place in RentorNG that decides what a tenant is charged. It is a
 * pure function: lease terms in, a list of invoices out. Nothing is written to a database here,
 * which is deliberate — the manager sees the proposed schedule, can edit it, and only then is it
 * committed. Billing that happens invisibly is billing nobody trusts.
 */

import { addDays, inclusiveDays, type PlainDate } from "./dates";
import { advance, cycleInfo, periodsFor, type CyclePeriod, type RentCycle } from "./cycles";
import { applyRate, proRata, sum, type BasisPoints, type Minor } from "./money";
import type { BeneficiaryRole } from "./beneficiaries";

export type ChargeType =
  | "rent"
  | "service_charge"
  | "deposit"
  | "agency_fee"
  | "legal_fee"
  | "late_fee"
  | "utility"
  | "maintenance_recharge"
  | "other";

export interface ChargeTypeInfo {
  value: ChargeType;
  label: string;
  /** Deposits are held on the tenant's behalf; they are a liability, never income. */
  income: boolean;
  /** Refundable at the end of the tenancy, subject to deductions. */
  refundable: boolean;
  /**
   * **This charge is made once per letting, not for each period of it.**
   *
   * It used to read "shown when creating a lease as a suggested commencement charge", and nothing
   * anywhere read it — which is how it came to be wrong on `service_charge` without anybody
   * noticing. It is now the rule that decides **what a renewal carries**, through
   * `carriesToRenewal` below, and that makes its classification a billing decision rather than a
   * hint about a form.
   *
   * The agency fee is the clearest case and the reason this matters: it is paid **on moving in,
   * once**. Ten moves, ten agency fees; ten years in one flat, one. The same is true of the legal
   * fee that drew the agreement and of the caution deposit, which is already held.
   */
  commencement: boolean;
  /**
   * Whose money this is when nobody has said.
   *
   * A **default for the form and a fall-back for rows written before the field existed**, never an
   * inference that overrides what somebody chose. See `beneficiaries.ts` — the destination of a
   * charge is stored on the charge, and this only supplies the first answer.
   */
  beneficiary: BeneficiaryRole;
}

/**
 * The charge types, and what each one means for billing.
 *
 * Two of these were classified wrongly and both were corrected together, because they are the same
 * mistake seen from the two ends:
 *
 * - **`service_charge` was `commencement: true`.** A service charge is the one thing on this list
 *   that most obviously *should* repeat with each rent — it pays for the cleaning and the
 *   generator, month after month — and marking it a once-per-letting charge would have made a
 *   renewal drop it and the building go unmaintained on the books.
 * - **`maintenance_recharge` was `commencement: false`.** A repair recharged is a past event, not
 *   a term of the tenancy. Carrying it into a renewal would bill the tenant again for a pump that
 *   was replaced last year.
 *
 * `late_fee` is a penalty on one specific lateness and never repeats by itself; `other` is the
 * agency's own charge and defaults to not repeating, because a charge the software cannot name is
 * a charge it must not raise again on its own initiative — the agency adds it by hand if it is
 * genuinely due, and `POST /invoices` never refuses them.
 *
 * The `beneficiary` column is the correction this table exists to carry: **rent belongs to the
 * landlord whole**, the caution deposit usually does and it is the agency's call, the agency fee
 * is the agency's income, and a legal fee belongs to whoever drew the agreement — which the
 * software cannot know, so it asks.
 */
export const CHARGE_TYPES: ChargeTypeInfo[] = [
  { value: "rent", label: "Rent", income: true, refundable: false, commencement: false, beneficiary: "landlord" },
  { value: "service_charge", label: "Service charge", income: true, refundable: false, commencement: false, beneficiary: "agency" },
  { value: "deposit", label: "Caution deposit", income: false, refundable: true, commencement: true, beneficiary: "landlord" },
  { value: "agency_fee", label: "Agency fee", income: true, refundable: false, commencement: true, beneficiary: "agency" },
  { value: "legal_fee", label: "Legal / agreement fee", income: true, refundable: false, commencement: true, beneficiary: "unassigned" },
  { value: "late_fee", label: "Late payment fee", income: true, refundable: false, commencement: true, beneficiary: "agency" },
  { value: "utility", label: "Utility", income: true, refundable: false, commencement: false, beneficiary: "agency" },
  { value: "maintenance_recharge", label: "Maintenance recharge", income: true, refundable: false, commencement: true, beneficiary: "agency" },
  { value: "other", label: "Other", income: true, refundable: false, commencement: true, beneficiary: "unassigned" },
];

/**
 * The type values as a tuple, for the one place a validator needs them.
 *
 * `POST /invoices` and the tenancy form each used to restate the nine strings in a `z.enum`, so the
 * list existed in three places and the two copies were already drifting — the endpoint that adds a
 * line to an existing invoice accepted six of the nine and refused the rest, which is a refusal
 * nobody decided on. Deriving the tuple here means adding a charge type is one edit.
 */
export const CHARGE_TYPE_VALUES = CHARGE_TYPES.map((c) => c.value) as [ChargeType, ...ChargeType[]];

const CHARGE_BY_VALUE = new Map(CHARGE_TYPES.map((c) => [c.value, c]));

export function chargeTypeInfo(type: ChargeType): ChargeTypeInfo {
  return (
    CHARGE_BY_VALUE.get(type) ?? {
      value: type,
      label: type,
      income: true,
      refundable: false,
      /* An unknown type is treated as once-per-letting, so a renewal does not carry something the
         software cannot describe. Never raising a charge again is recoverable by hand; raising one
         every year that nobody agreed is a bill the tenant disputes. */
      commencement: true,
      beneficiary: "unassigned",
    }
  );
}

/** Whose money a charge of this type is, when the charge itself does not say. */
export function defaultBeneficiaryRole(type: ChargeType): BeneficiaryRole {
  return chargeTypeInfo(type).beneficiary;
}

/**
 * Whether a charge on an ending tenancy is carried into its renewal.
 *
 * **This is a live billing correction, not a refinement.** The renewal endpoint used to drop the
 * caution deposit and nothing else, with a comment showing the right instinct applied to exactly
 * one charge — so a renewing tenant was re-charged the **agency fee** and the **legal fee** every
 * single year, for a letting they were not making. The operator, asked directly whether the legal
 * fee is an exception: *"No. No fee is charged again on renewal."*
 *
 * Two clauses, and the order is the point:
 *
 * 1. **A charge the agency itself said repeats, repeats.** `frequency: "per_period"` is somebody
 *    stating that this is billed alongside every rent, and that statement outranks anything the
 *    type can be assumed to mean. It is also the only way an `other` charge — an estate levy, a
 *    security subscription — can be carried, since the software cannot know what it is.
 * 2. **Otherwise the type decides**, and a charge made once per letting is not made again.
 *
 * What this rule is not: a wall. It governs what happens **automatically**. An agency may always
 * add a charge to the next invoice by hand — a fee genuinely agreed again, a repair recharged,
 * something with no type at all — and `POST /invoices` stays open precisely so that this rule can
 * be right by default without ever becoming a refusal.
 */
export function carriesToRenewal(charge: Pick<LeaseCharge, "type" | "frequency">): boolean {
  if (charge.frequency === "per_period") return true;
  return !chargeTypeInfo(charge.type).commencement;
}

/** The same rule over a tenancy's whole charge list, which is how a renewal actually asks it. */
export function renewalCharges<T extends Pick<LeaseCharge, "type" | "frequency">>(charges: readonly T[] | null | undefined): T[] {
  return (charges ?? []).filter((charge) => carriesToRenewal(charge));
}

/**
 * How long a name somebody may give an "Other" charge.
 *
 * Sixty characters is a table cell, an invoice line and a price breakdown on a phone. It is not a
 * description — the charge already has an amount and a basis beside it, and a paragraph typed here
 * would be truncated by every one of the six places this appears rather than by the one place it
 * is entered. Enforced on the way in as well as here, so nothing longer is ever stored.
 */
export const CHARGE_LABEL_MAX = 60;

/**
 * The name of a charge on anything a person reads.
 *
 * **`other` is the one charge type that does not say what it is.** Every other type names itself —
 * "Caution deposit", "Legal / agreement fee" — and a tenant looking at an invoice knows what they
 * are being asked for. "Other" tells them nothing, and it turns up on the tenancy form, the
 * invoice and its lines, the receipt, the statement, the public price breakdown and the portal.
 * Six places to be told nothing. So it carries a name of the agency's own, and this is the only
 * function that decides what to show.
 *
 * Two rules, and both are here rather than at each of the six call sites:
 *
 * - **"Other" is the default, never a blank.** A charge raised before this field existed, and one
 *   whose box was opened and left empty, both read exactly as they always did. That is what lets
 *   the column be added with no back-fill: null already means the right thing.
 * - **A label only ever names an `other` charge.** Somebody types "Estate development levy", then
 *   changes the type to a service charge — the name goes with the type it named. Honouring a stray
 *   label would rename a caution deposit on an invoice the tenant keeps.
 */
export function chargeLabel(type: ChargeType, label?: string | null): string {
  if (type === "other") {
    const named = (label ?? "").trim().slice(0, CHARGE_LABEL_MAX).trim();
    if (named) return named;
  }
  return chargeTypeInfo(type).label;
}

/**
 * The label as it should be *stored*: the agency's words, or nothing at all.
 *
 * The read-side rule above is what every screen goes through, so this is belt as well as braces —
 * but a label left on a charge whose type has changed is a row that says one thing and renders
 * another, and rows outlive the code that wrote them. `undefined` rather than `""`, so a charge
 * nobody named has no key at all and JSON stored today matches JSON stored before the field.
 */
export function normaliseChargeLabel(type: ChargeType, label?: string | null): string | undefined {
  if (type !== "other") return undefined;
  const named = (label ?? "").trim().slice(0, CHARGE_LABEL_MAX).trim();
  return named || undefined;
}

/**
 * A charge attached to a lease that is not the rent itself.
 *
 * `basis` is what makes this usable: agency fee is quoted as a percentage of the rent, legal fee
 * is usually a flat figure, and both appear on the same tenancy. Storing the basis rather than
 * only the resolved amount means a rent review recalculates the fees correctly.
 */
export interface LeaseCharge {
  type: ChargeType;
  label?: string;
  basis: "fixed" | "percent_of_rent";
  /** Minor units when `fixed`, basis points when `percent_of_rent`. */
  value: number;
  /** `once` bills at commencement only; `per_period` bills alongside every rent period. */
  frequency: "once" | "per_period";
  /**
   * Whose money this charge is — a `beneficiaries` row belonging to the agency.
   *
   * Resolved from `defaultBeneficiaryRole` when the tenancy is recorded and stored on the charge,
   * so a beneficiary later renamed, archived or repointed does not silently rewrite what a tenant
   * was told at the time. Absent on a charge written before beneficiaries existed, which is why
   * every reader falls back to the role rather than treating it as an error.
   */
  beneficiaryId?: string | null;
}

/**
 * A tenancy's charges as they should be *stored*.
 *
 * The counterpart of `normaliseCharges` in `portfolio.ts`, which does the same job for a unit's
 * asking charges. Both exist because a name is entered once and then read in six places, and the
 * rule about which charges may carry one has to be applied where the row is written rather than at
 * each of the six — a row outlives the code that wrote it, and every reader after that inherits
 * whatever was stored.
 *
 * It only touches the label. Amounts, bases and frequencies on a tenancy are somebody's agreement
 * with a tenant and are not this function's to tidy.
 */
export function normaliseLeaseCharge<T extends { type: ChargeType; label?: string | null }>(charge: T): T {
  const label = normaliseChargeLabel(charge.type, charge.label);
  /* The key is dropped rather than set to undefined, so what is stored for a charge nobody named is
     byte for byte what was stored before this field existed. */
  if (label === undefined) {
    const { label: _dropped, ...rest } = charge;
    return rest as T;
  }
  return { ...charge, label };
}

/** The same rule over a list, which is how a tenancy's charges arrive and are stored. */
export function normaliseLeaseCharges<T extends { type: ChargeType; label?: string | null }>(
  charges: readonly T[] | null | undefined,
): T[] {
  return (charges ?? []).map(normaliseLeaseCharge);
}

export interface DraftLine {
  chargeType: ChargeType;
  description: string;
  amount: Minor;
  periodStart?: PlainDate;
  periodEnd?: PlainDate;
  /**
   * Whose money this line is.
   *
   * Carried from the charge onto the invoice line rather than looked up again when the payout is
   * drawn, for the same reason a payout line keeps its own reference: the invoice is what the
   * tenant was shown, and what it said must not change because a beneficiary was edited afterwards.
   */
  beneficiaryId?: string | null;
}

export interface DraftInvoice {
  /** Stable within a schedule, so an edit can be matched back after a recalculation. */
  key: string;
  issueDate: PlainDate;
  dueDate: PlainDate;
  periodStart?: PlainDate;
  periodEnd?: PlainDate;
  lines: DraftLine[];
  total: Minor;
}

export interface LeaseTerms {
  start: PlainDate;
  end?: PlainDate;
  /** The rent for ONE cycle — not annualised. An annual lease at 1.2m is `rentAmount: 120000000`. */
  rentAmount: Minor;
  cycle: RentCycle;
  /** How many cycles make one period. 1 unless the term says otherwise. */
  every?: number;
  terms?: "advance" | "arrears";
  /** Days after the due date before the money counts as late. */
  graceDays?: number;
  charges?: LeaseCharge[];
  /** Bill the first period pro-rata from the start date to the end of the calendar cycle. */
  proRateFirstPeriod?: boolean;
  /**
   * Whose the rent is. The landlord's, in every ordinary case.
   *
   * On the terms rather than on a charge because rent is not one of `charges` — it is the tenancy
   * itself, generated for every period from the cycle. Left unset, the rent line carries no
   * beneficiary and the payout run falls back to the owner of the unit, which is the same answer
   * by a different route and is what every tenancy recorded before this field does.
   */
  rentBeneficiaryId?: string | null;
}

/**
 * Expand lease terms into the invoices they generate.
 *
 * Commencement charges (deposit, agency fee, legal fee) land on a single invoice dated the
 * lease start, because that is how they are collected in practice — one payment, one receipt,
 * before the keys change hands. Rent for the first period is on that same invoice when the
 * terms are "in advance".
 */
export function buildSchedule(lease: LeaseTerms): DraftInvoice[] {
  const periods = periodsFor({
    start: lease.start,
    end: lease.end,
    cycle: lease.cycle,
    every: lease.every,
    terms: lease.terms ?? "advance",
  });

  const invoices: DraftInvoice[] = [];
  const onceCharges = (lease.charges ?? []).filter((c) => c.frequency === "once");
  const perPeriodCharges = (lease.charges ?? []).filter((c) => c.frequency === "per_period");

  periods.forEach((period, i) => {
    const lines: DraftLine[] = [];
    const rent = rentForPeriod(lease, period);

    lines.push({
      chargeType: "rent",
      description: rentDescription(lease.cycle, period),
      amount: rent,
      periodStart: period.start,
      periodEnd: period.end,
      beneficiaryId: lease.rentBeneficiaryId ?? null,
    });

    for (const charge of perPeriodCharges) {
      const amount = resolveCharge(charge, rent);
      if (amount === 0) continue;
      lines.push({
        chargeType: charge.type,
        description: chargeLabel(charge.type, charge.label),
        amount,
        periodStart: period.start,
        periodEnd: period.end,
        beneficiaryId: charge.beneficiaryId ?? null,
      });
    }

    if (i === 0) {
      for (const charge of onceCharges) {
        const amount = resolveCharge(charge, rent);
        if (amount === 0) continue;
        lines.push({
          chargeType: charge.type,
          description: chargeLabel(charge.type, charge.label),
          amount,
          beneficiaryId: charge.beneficiaryId ?? null,
        });
      }
    }

    invoices.push({
      key: `p${period.index}`,
      issueDate: issueDateFor(period, lease),
      dueDate: period.dueDate,
      periodStart: period.start,
      periodEnd: period.end,
      lines,
      total: sum(lines.map((l) => l.amount)),
    });
  });

  return invoices;
}

/**
 * Invoices are raised ahead of the due date so the tenant has warning — a month for long
 * cycles, a week for short ones. Never before the lease is signed, though.
 */
function issueDateFor(period: CyclePeriod, lease: LeaseTerms): PlainDate {
  const months = cycleInfo(lease.cycle).months ?? 0;
  const lead = months >= 6 ? 30 : months >= 3 ? 14 : 7;
  const issue = addDays(period.dueDate, -lead);
  return issue < lease.start ? lease.start : issue;
}

function rentForPeriod(lease: LeaseTerms, period: CyclePeriod): Minor {
  if (!period.partial && !(lease.proRateFirstPeriod && period.index === 0)) return lease.rentAmount;
  const fullEnd = addDays(advance(period.start, lease.cycle), -1);
  const fullDays = inclusiveDays(period.start, fullEnd);
  return proRata(lease.rentAmount, period.days, fullDays);
}

function rentDescription(cycle: RentCycle, period: CyclePeriod): string {
  const noun = cycle === "one_off" ? "Rent" : "Rent";
  return `${noun} · ${period.start} to ${period.end}${period.partial ? " (pro-rata)" : ""}`;
}

export function resolveCharge(charge: LeaseCharge, rent: Minor): Minor {
  return charge.basis === "percent_of_rent" ? applyRate(rent, charge.value as BasisPoints) : charge.value;
}

/** The commencement total a manager quotes on the phone: first rent plus every one-off charge. */
export function commencementTotal(lease: LeaseTerms): Minor {
  const schedule = buildSchedule(lease);
  return schedule.length ? schedule[0].total : 0;
}

/** Total contracted value of a lease across its whole term. Used on the lease header and in reports. */
export function contractValue(lease: LeaseTerms): Minor {
  return sum(buildSchedule(lease).map((i) => i.total));
}

export interface InvoiceLike {
  id: string;
  dueDate: PlainDate;
  total: Minor;
  paid: Minor;
  status: InvoiceStatus;
}

export type InvoiceStatus = "draft" | "open" | "part_paid" | "paid" | "overdue" | "void" | "written_off";

export function balanceOf(invoice: Pick<InvoiceLike, "total" | "paid">): Minor {
  return invoice.total - invoice.paid;
}

/**
 * The status an invoice *should* have, given what has been paid and what day it is.
 *
 * Derived rather than stored-and-hoped-for: a nightly job recomputes it, and any read path can
 * call this to be certain a screen is not showing a stale "open" on something that went overdue
 * at midnight.
 */
export function deriveStatus(
  invoice: { total: Minor; paid: Minor; dueDate: PlainDate; status: InvoiceStatus },
  asOf: PlainDate,
  graceDays = 0,
): InvoiceStatus {
  if (invoice.status === "void" || invoice.status === "written_off" || invoice.status === "draft") {
    return invoice.status;
  }
  const balance = balanceOf(invoice);
  if (balance <= 0) return "paid";
  const lateFrom = addDays(invoice.dueDate, graceDays);
  if (asOf > lateFrom) return "overdue";
  return invoice.paid > 0 ? "part_paid" : "open";
}

export function isSettled(status: InvoiceStatus): boolean {
  return status === "paid" || status === "void" || status === "written_off";
}

/**
 * Allocate a payment across invoices, oldest due date first.
 *
 * Oldest-first is the default because it is what both sides expect and it clears the arrear
 * that is accruing late fees. The manager can override it per payment; anything left over
 * becomes account credit rather than being refused, because money already received must never
 * be turned away by the software.
 */
export function allocateOldestFirst(
  amount: Minor,
  invoices: InvoiceLike[],
): { allocations: Array<{ invoiceId: string; amount: Minor }>; credit: Minor } {
  const open = invoices
    .filter((i) => !isSettled(i.status) && balanceOf(i) > 0)
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : a.id < b.id ? -1 : 1));

  const allocations: Array<{ invoiceId: string; amount: Minor }> = [];
  let left = amount;

  for (const invoice of open) {
    if (left <= 0) break;
    const take = Math.min(left, balanceOf(invoice));
    if (take <= 0) continue;
    allocations.push({ invoiceId: invoice.id, amount: take });
    left -= take;
  }

  return { allocations, credit: left };
}

/** Deposit reconciliation at the end of a tenancy. */
export function settleDeposit(
  held: Minor,
  deductions: Array<{ reason: string; amount: Minor }>,
): { deducted: Minor; refund: Minor; shortfall: Minor } {
  const deducted = sum(deductions.map((d) => d.amount));
  return {
    deducted,
    refund: Math.max(0, held - deducted),
    shortfall: Math.max(0, deducted - held),
  };
}
